// src/lib/composeStore.js — this device's copies of compose windows and of
// sends that have not been confirmed yet (localStorage). The shapes and rules
// are src/lib/composeDraft.js; this is only where they are kept.

import {
  COMPOSE_DRAFT_PREFIX, PENDING_SEND_PREFIX, pendingKeyFor,
  snapshotForDevice, isFreshDeviceCopy,
} from './composeDraft'

// ── Device copies ───────────────────────────────────────────────────────────

// Ids of every device-backed compose — the provider reopens these as
// minimized docked windows on app load ("leave and come back").
export function listLocalComposeDrafts() {
  const ids = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k) continue
      if (k === COMPOSE_DRAFT_PREFIX) ids.push('legacy')
      else if (k.startsWith(`${COMPOSE_DRAFT_PREFIX}:`)) ids.push(k.slice(COMPOSE_DRAFT_PREFIX.length + 1))
    }
  } catch { /* localStorage unavailable */ }
  return ids
}

// Sends that were handed off but never confirmed (the tab closed during the
// undo window, or the send was rejected while no window was open). The
// provider reopens them as drafts carrying a notice, so nothing is lost and
// the person checks Sent before sending again.
export function listPendingSends() {
  const out = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k || !k.startsWith(`${PENDING_SEND_PREFIX}:`)) continue
      const rec = JSON.parse(localStorage.getItem(k) || 'null')
      if (rec?.snapshot && isFreshDeviceCopy(rec)) out.push({ sendKey: k.slice(PENDING_SEND_PREFIX.length + 1), ...rec })
    }
  } catch { /* localStorage unavailable */ }
  return out
}

export function writeDevice(key, snap) {
  try { localStorage.setItem(key, JSON.stringify(snapshotForDevice(snap))); return true } catch { return false }
}
export function clearDevice(key) {
  try { localStorage.removeItem(key) } catch { /* noop */ }
}
export function readDevice(key) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const d = JSON.parse(raw)
    if (!isFreshDeviceCopy(d)) { clearDevice(key); return null }
    return d
  } catch { return null }
}
export function writePendingSend(sendKey, snapshot, state = 'waiting') {
  try { localStorage.setItem(pendingKeyFor(sendKey), JSON.stringify({ snapshot: snapshotForDevice(snapshot), state, savedAt: Date.now() })) } catch { /* noop */ }
}
export function clearPendingSend(sendKey) {
  if (sendKey) clearDevice(pendingKeyFor(sendKey))
}


// The offline queue stopped retrying a send. Its snapshot becomes a
// recoverable draft again (the provider opens it), keeping the send key so a
// resend is still checked against Sent. state: 'rejected' (Gmail refused it —
// nothing went out) or 'unconfirmed' (retries ran out — it may have gone).
export const PENDING_SEND_RETURNED_EVENT = 'chatrone:pending-send-returned'
export function givePendingSendBack(sendKey, state, reason = null) {
  if (!sendKey) return false
  try {
    const key = pendingKeyFor(sendKey)
    const rec = JSON.parse(localStorage.getItem(key) || 'null')
    if (!rec?.snapshot) return false
    localStorage.setItem(key, JSON.stringify({ ...rec, state, reason: reason ? String(reason).slice(0, 300) : null, savedAt: Date.now() }))
    try { window.dispatchEvent(new CustomEvent(PENDING_SEND_RETURNED_EVENT, { detail: { sendKey } })) } catch { /* noop */ }
    return true
  } catch { return false }
}
